import { useEffect, useEffectEvent, useMemo, type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Renew } from '@mint-lib/icons';
import { enqueueSnackbar, toast } from '@mint-lib/notifications';
import { useProfile } from '@mint-lib/profile-helpers';
import { useQueryClient } from '@mint-lib/query-client';
import { Button } from '@mint-lib/tw-components';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';

import useFlowProcessDetailsData from '../../../api/queries/useFlowProcessDetailsData.js';
import { EntityStatuses } from '../../../constants/shared.js';
import { ApprovalSequenceType } from '../../../types/shared.js';
import { mapApprovalProcessToSequence } from '../../../utils/shared.js';
import ApprovalSequence from '../../ApprovalSequence.jsx';
import ApprovalSequenceSkeleton from '../../ApprovalSequenceSkeleton.jsx';

type Props = {
  isModalOpen: boolean;
  approvalProcessId: string | null;
  userStatus: EntityStatuses;
};

const StatusDetailsModalContent: FC<Props> = ({
  isModalOpen,
  approvalProcessId,
  userStatus,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const queryClient = useQueryClient();

  const {
    data: processDetails,
    isPending,
    isError,
    isEnabled,
  } = useFlowProcessDetailsData({
    enabled: isModalOpen,
    approvalProcessId,
  });

  const { profile } = useProfile();
  const currentUserId = profile?.id;

  const approvalSequence = useMemo<ApprovalSequenceType | null>(() => {
    if (!processDetails || !currentUserId) {
      return null;
    }

    return mapApprovalProcessToSequence(processDetails, currentUserId);
  }, [processDetails, currentUserId]);

  const showPendingState =
    isPending ||
    isError ||
    !currentUserId ||
    !approvalSequence?.length ||
    !approvalProcessId;

  const showErrorNotification = useEffectEvent(() => {
    const id = crypto.randomUUID();

    const handleRetry = () => {
      queryClient.resetQueries({
        queryKey: ApprovalFlows.getRequest({
          urlParams: {
            requestId: approvalProcessId ?? '',
          },
        }).queryKey,
      });
      toast.dismiss(id);
    };

    enqueueSnackbar({
      variant: 'error',
      title: t('Could not load approval data.'),
      actions: (
        <Button
          data-dd-action-name="Retry action"
          className="pointer-events-auto"
          variant="outlined"
          color="error"
          size="small"
          onClick={handleRetry}
        >
          <Renew className="size-4" />
          {t('Try again')}
        </Button>
      ),
      removable: false,
      id,
    });
  });

  useEffect(() => {
    if (!isError || !isEnabled) {
      return;
    }

    showErrorNotification();
  }, [isError, isEnabled]);

  if (showPendingState) {
    return (
      <ApprovalSequenceSkeleton
        aria-label={
          isError
            ? 'Approval sequence loading failed'
            : 'Loading approval sequence'
        }
      />
    );
  }

  return (
    <ApprovalSequence
      items={approvalSequence}
      approvalProcessId={approvalProcessId}
      reminderSendingAllowed={userStatus === 'requester'}
    />
  );
};

export default StatusDetailsModalContent;
